import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Settings, Calendar, Wrench, TrendingUp, Check } from 'lucide-react';
import SignupFormModal from '../components/SignupFormModal';

export default function HelpCenterPage() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
    const [modalButtonType, setModalButtonType] = useState('demo');

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const helpTopics = [
        {
            Icon: Settings,
            title: "Setup Guides",
            color: "bg-pink-100",
            iconColor: "text-pink-600",
            items: [
                { title: "How to connect your whatsapp snd email", description: "Step-by-step guide to integrate WhatsApp Business API and email accounts with Qorvix." },
                { title: "How to link your CRM and ads", description: "Connect HubSpot, Salesforce, Google Ads, and Meta Ads for seamless lead capture." },
                { title: "How to train your AI with your tone and offers", description: "Customize AI responses to match your brand voice and product knowledge." }
            ]
        },
        {
            Icon: Calendar,
            title: "Daily Operations",
            color: "bg-cyan-100",
            iconColor: "text-cyan-600",
            items: [
                { title: "Tracking live conversations", description: "Monitor all customer interactions in real-time through your dashboard." },
                { title: "Adjusting follow-up timing", description: ". Configure automated follow-up schedules based on customer behavior." },
                { title: "Managing appointments and cancellations", description: "Handle bookings, reschedules, and cancellations automatically." }
            ]
        },
        {
            Icon: Wrench,
            title: "Troubleshooting",
            color: "bg-pink-100",
            iconColor: "text-pink-600",
            items: [
                { title: "Fixing message sync issues", description: "Resolve common synchronization problems with WhatsApp and email." },
                { title: "Re-training an AI reply", description: "Update and improve AI responses when they don't match expectations." },
                { title: "Handling failed ad integrations", description: "Troubleshoot connection issues with Google Ads, Meta, and other platforms." }
            ]
        },
        {
            Icon: TrendingUp,
            title: "Analytics",
            color: "bg-gray-100",
            iconColor: "text-gray-600",
            items: [
                { title: "Viewing conversion rate in real time", description: "Track how many leads are converting to appointments and sales." },
                { title: "Comparing campaigns", description: "Analyze performance across different ad campaigns and channels." },
                { title: "Downloading lead data instantly", description: "Export conversation transcripts, lead information, and analytics reports." }
            ]
        }
    ];

    const supportLevels = [
        {
            title: "Standard",
            subtitle: "24-hour support",
            description: "Email and ticket-based support with response within 24 hours.",
            features: [
                "Help center access",
                "Email automation",
                "Video tutorials",
                "Documentation"
            ]
        },
        {
            title: "Premium",
            subtitle: "Priority under 1 hour",
            description: "Priority support with dedicated chat and phone assistance.",
            features: [
                "All Standard features",
                "Live chat support",
                "Phone support",
                "Priority queue",
                "Advanced analytics training"
            ]
        },
        {
            title: "Enterprise",
            subtitle: "Dedicated team",
            description: "White-glove service with dedicated AI manager and success engineer.",
            features: [
                "All Premium features",
                "Dedicated AI manage",
                "Success engineer",
                "Custom training sessions",
                "Quarterly strategy calls",
                "24/7 priority hotline"
            ]
        }
    ];

    const quickLinks = [
        { label: "How it works", path: "/how-it-works" },
        { label: "Integrations", path: "/integrations" },
        { label: "FAQs", path: "/faq" },
        { label: "Roadmap", path: "/roadmap" }
    ];

    return (
        <div className="min-h-screen bg-white font-['Outfit',sans-serif] text-gray-900">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        
        .text-gradient {
          background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

            {/* Navigation */}
            <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <Link to="/" className="flex items-center gap-2">
                            <img
                                src="/logo-qorvix.png"
                                alt="Qorvix Logo"
                                className="h-10 w-auto"
                            />
                        </Link>
                        <div className="hidden lg:flex items-center space-x-8">
                            <Link to="/" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Home</Link>
                            <Link to="/about" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">About</Link>
                            <Link to="/how-it-works" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">How it works</Link>
                            <Link to="/portfolio" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Portfolio</Link>
                            <div className="relative group">
                                <button className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px] flex items-center gap-1">
                                    Resources
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                    <div className="py-2">
                                        <Link to="/documentation" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Documentation</Link>
                                        <Link to="/integrations" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Integration</Link>
                                        <Link to="/faq" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">FAQs</Link>
                                        <Link to="/help-center" className="block px-4 py-2.5 text-orange-500 bg-gray-50 transition-colors">Help center</Link>
                                        <Link to="/roadmap" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Roadmap</Link>
                                    </div>
                                </div>
                            </div>
                            <Link to="/contact" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Contact us</Link>
                            <button onClick={() => {
                                setModalButtonType('demo');
                                setIsSignupModalOpen(true);
                            }} className="bg-transparent border-2 border-orange-500 text-orange-500 px-6 py-2.5 rounded-lg hover:bg-orange-500 hover:text-white transition-all font-medium text-[15px]">
                                Start Free Demo
                            </button>
                        </div>

                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden text-gray-900"
                        >
                            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Modal - Add this after </nav> */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>

                    {/* Menu Panel */}
                    <div className="fixed top-8 left-1/2 -translate-x-1/2 w-[90%] max-w-sm bg-white rounded-3xl shadow-2xl p-8 z-50">
                        {/* Close Button */}
                        <button
                            onClick={() => setMobileMenuOpen(false)}
                            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <X size={24} />
                        </button>

                        {/* Menu Items */}
                        <nav className="mt-4 space-y-6">
                            <Link
                                to="/"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Home
                            </Link>
                            <Link
                                to="/about"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                About
                            </Link>
                            <Link
                                to="/how-it-works"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                How it works
                            </Link>
                            <Link
                                to="/portfolio"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                portfolio
                            </Link>

                            {/* Resources Dropdown */}
                            <div>
                                <button className="w-full text-left text-lg font-medium text-gray-900 flex items-center gap-2">
                                    resources
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className="mt-1">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <div className="mt-4 ml-4 space-y-4">
                                    <Link
                                        to="/documentation"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Documentation
                                    </Link>
                                    <Link
                                        to="/integrations"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Integrations
                                    </Link>
                                    <Link
                                        to="/faq"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        FAQs
                                    </Link>
                                    <Link
                                        to="/help-center"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Help center
                                    </Link>
                                    <Link
                                        to="/roadmap"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Roadmap
                                    </Link>
                                </div>
                            </div>

                            <Link
                                to="/contact"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Contact us
                            </Link>

                            {/* CTA Button */}
                            <button onClick={() => {
                                setModalButtonType('demo');
                                setIsSignupModalOpen(true);
                            }} className="text-lg font-medium text-orange-500 border-b-2 border-orange-500 pb-1">
                                Start free demo
                            </button>
                        </nav>
                    </div>
                </div>
            )}
            {/* Hero Section */}
            <section className="pt-32 pb-32 px-4 bg-gradient-to-br from-orange-50 via-white to-orange-50">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-5xl md:text-6xl font-bold mb-6">
                        Help <span className="text-gradient">Center</span>
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                        Everything you need to set up, manage, and scale with Qorvix. From quick start guides to advanced troubleshooting.
                    </p>
                </div>
            </section>

            {/* Browse Help Topics */}
            <section className="py-32 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-20">
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">
                            Browse <span className="text-gradient">Help Topics</span>
                        </h2>
                        <p className="text-gray-600">Find guides, tutorials, and support resources</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-10">
                        {helpTopics.map((topic, index) => {
                            const IconComponent = topic.Icon;
                            return (
                                <div key={index} className="bg-gray-50 p-10 rounded-2xl">
                                    <div className={`w-20 h-20 ${topic.color} rounded-full flex items-center justify-center mb-8`}>
                                        <IconComponent className={topic.iconColor} size={32} />
                                    </div>
                                    <h3 className="text-2xl font-bold mb-8">{topic.title}</h3>
                                    <div className="space-y-6">
                                        {topic.items.map((item, itemIndex) => (
                                            <div key={itemIndex}>
                                                <div className="flex items-start gap-3 mb-2">
                                                    <div className="w-5 h-5 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                        <Check size={12} className="text-white" strokeWidth={3} />
                                                    </div>
                                                    <h4 className="font-bold">{item.title}</h4>
                                                </div>
                                                <p className="text-sm text-gray-600 ml-8">{item.description}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Support Levels */}
            <section className="py-32 px-4 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-20">
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">
                            Support <span className="text-gradient">Levels</span>
                        </h2>
                        <p className="text-gray-600">Choose the level of support that fits your needs</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {supportLevels.map((level, index) => (
                            <div key={index} className="bg-white p-8 rounded-2xl border border-gray-200">
                                <h3 className="text-2xl font-bold mb-2">{level.title}</h3>
                                <p className="text-sm font-semibold text-gray-700 mb-4">{level.subtitle}</p>
                                <p className="text-sm text-gray-600 mb-8 leading-relaxed">{level.description}</p>
                                <div className="space-y-3">
                                    {level.features.map((feature, featureIndex) => (
                                        <div key={featureIndex} className="flex items-center gap-3">
                                            <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                                                <Check size={12} className="text-white" strokeWidth={3} />
                                            </div>
                                            <span className="text-sm text-gray-700">{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quick Links */}
            <section className="py-20 px-4 bg-white">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl font-bold mb-4">
                        Quick <span className="text-gradient">Links</span>
                    </h2>
                    <p className="text-gray-600 mb-12">Popular Resources And Guides</p>
                    <div className="flex flex-wrap justify-center gap-4">
                        {quickLinks.map((link, index) => (
                            <Link
                                key={index}
                                to={link.path}
                                className="px-8 py-3 bg-orange-50 border-2 border-orange-200 text-orange-600 rounded-lg hover:bg-orange-100 transition-all font-medium"
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Still Need Help */}
            <section className="py-20 px-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-400 via-orange-300 to-purple-400"></div>
                <div className="max-w-4xl mx-auto relative z-10">
                    <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800">
                        Still Need Help?
                    </h2>
                    <p className="text-lg text-gray-700 mb-8">
                        Our Support Team Is Available 24/7 To Assist You With Any Questions
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <Link
                            to="/contact"
                            className="bg-orange-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-orange-700 transition-all"
                        >
                            Contact Support
                        </Link>
                        <Link
                            to="/help-center"
                            className="border-2 border-white bg-white text-gray-800 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all"
                        >
                            Visit Help Center
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-xl font-bold mb-4">
                                <span className="text-gradient">Qorvix</span> — Automate What Matters
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-6">
                                Qorvix helps businesses automate workflows, connect tools, and scale faster using AI-powered systems. From CRM integrations to WhatsApp automations — we build smart, no-code solutions that work.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Contact</h4>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>London, England, UK</p>
                                <p>+44 7757 233260</p>
                                <p>Support@qorvix.ai</p>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Company</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/" className="block text-gray-600 hover:text-gray-900 transition-colors">Home</Link>
                                <Link to="/about" className="block text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                                <Link to="/portfolio" className="block text-gray-600 hover:text-gray-900 transition-colors">Portfolio</Link>
                                <Link to="/contact" className="block text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                                <Link to="/how-it-works" className="block text-gray-600 hover:text-gray-900 transition-colors">How it works</Link>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Resources</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/documentation" className="block text-gray-600 hover:text-gray-900 transition-colors">Documentation</Link>
                                <Link to="/integrations" className="block text-gray-600 hover:text-gray-900 transition-colors">Integration</Link>
                                <Link to="/faq" className="block text-gray-600 hover:text-gray-900 transition-colors">FAQs</Link>
                                <Link to="/help-center" className="block text-gray-600 hover:text-gray-900 transition-colors">Help Center</Link>
                                <Link to="/roadmap" className="block text-gray-600 hover:text-gray-900 transition-colors">Roadmap</Link>
                            </div>
                        </div>
                    </div>

                    <div className="pt-8 flex items-center justify-between border-t border-gray-200">
                        <div className="text-2xl font-bold text-gradient">Qorvix</div>
                        <div className="text-sm text-gray-600">
                            © Qorvix Technologies. All Rights Reserved. Built with purpose and precision by <span className="font-semibold">Qorvix Team</span>.
                        </div>
                    </div>
                </div>
            </footer>
            {/* Signup Modal */}
            <SignupFormModal
                isOpen={isSignupModalOpen}
                onClose={() => setIsSignupModalOpen(false)}
                buttonType={modalButtonType}
            />
        </div>
    );
}